import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ShieldCheck, Eye, EyeOff, Save, Trash2, Loader2,
  CheckCircle, XCircle, ExternalLink, RefreshCw, Info,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json',
});

const AttrestrSettings = () => {
  const { toast } = useToast();
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/settings/attestr`, { headers: authHeaders() });
      if (res.ok) setConfig(await res.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    if (!token.trim()) {
      toast({ title: 'Validation Error', description: 'Token cannot be empty', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/settings/attestr`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ token: token.trim() }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast({ title: 'Saved', description: 'Attestr token saved securely (AES encrypted).' });
      setToken('');
      await fetchConfig();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the Attestr token? GST lookup will be disabled.')) return;
    setRemoving(true);
    try {
      await fetch(`${API}/api/settings/attestr`, { method: 'DELETE', headers: authHeaders() });
      toast({ title: 'Removed', description: 'Attestr token removed.' });
      setConfig(prev => ({ ...prev, configured: false, masked: null }));
    } catch {}
    setRemoving(false);
  };

  const handleClearCache = async () => {
    if (!window.confirm('Clear all cached GST lookup data?')) return;
    setClearing(true);
    try {
      const res = await fetch(`${API}/api/gst/cache`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        toast({ title: 'Cache cleared', description: d.message });
      }
    } catch {}
    setClearing(false);
  };

  return (
    <div className="space-y-6" data-testid="attestr-settings-page">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7 text-accent-blue" />
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">Attestr API</h2>
          <p className="text-text-secondary font-ibm text-sm mt-0.5">
            GST lookup integration — fetch company details and branches by name
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="flex gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm font-ibm text-blue-800">
          <p className="font-semibold mb-1">How it works</p>
          <ul className="space-y-1 text-blue-700 list-disc list-inside">
            <li>Enter your Attestr API token below — it is stored <strong>AES-encrypted</strong> and never exposed</li>
            <li>In Customer or Vendor forms, click <strong>"Search GST"</strong> next to the Business Name field</li>
            <li>Results are cached for <strong>7 days</strong> — same company is not re-fetched</li>
            <li>All GSTIN details, branches, and address are auto-filled from the API</li>
          </ul>
          <a
            href="https://attestr.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-blue-600 hover:text-blue-800 font-semibold"
          >
            Get your token at attestr.com <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Status Card */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text-dark font-ibm text-base">Token Status</h3>
          <button onClick={fetchConfig} className="text-text-secondary hover:text-accent-blue">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-text-secondary font-ibm text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking status...
          </div>
        ) : config?.configured ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-700 font-ibm text-sm">Token configured</p>
                <p className="text-xs text-gray-400 font-ibm font-mono">{config.masked}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="attestr-remove-btn"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={removing}
                className="text-red-600 border-red-200 hover:bg-red-50 font-ibm"
              >
                {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Remove token
              </Button>
              <Button
                data-testid="attestr-clear-cache-btn"
                variant="outline"
                size="sm"
                onClick={handleClearCache}
                disabled={clearing}
                className="font-ibm text-text-secondary"
              >
                {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Clear cache
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <p className="font-semibold text-gray-500 font-ibm text-sm">Not configured</p>
              <p className="text-xs text-gray-400 font-ibm">Add your token below to enable GST lookup</p>
            </div>
          </div>
        )}
      </Card>

      {/* Token Input Card */}
      <Card className="p-6 bg-white border border-gray-100 shadow-sm rounded-xl">
        <h3 className="font-semibold text-text-dark font-ibm text-base mb-4">
          {config?.configured ? 'Update Token' : 'Add Token'}
        </h3>
        <div className="space-y-4 max-w-lg">
          <div className="space-y-2">
            <Label className="font-ibm font-medium text-text-dark">
              Attestr API Token
              <span className="ml-2 text-xs text-gray-400 font-normal">(stored AES-encrypted, never exposed)</span>
            </Label>
            <div className="relative">
              <Input
                data-testid="attestr-token-input"
                type={showToken ? 'text' : 'password'}
                placeholder="Paste your Attestr token here..."
                value={token}
                onChange={e => setToken(e.target.value)}
                className="border-gray-200 font-mono pr-10 focus:border-accent-blue"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowToken(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 font-ibm">
              Your token looks like: <span className="font-mono">T1gwbWZ3...U0Mg==</span>
            </p>
          </div>
          <Button
            data-testid="attestr-save-btn"
            onClick={handleSave}
            disabled={saving || !token.trim()}
            className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-10 px-6 rounded-lg"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Token</>}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AttrestrSettings;
